import type {PosterProductDataForProcessDesign} from '@Panels/poster-publishing-options-panel/poster-publishing-options-panel.types';
import {Status} from '@Panels/poster-publishing-options-panel/poster-publishing-options-panel.types';
import {showErrorOnPanel} from '@Panels/poster-publishing-options-panel/poster-publishing-options-panel-library';
import type {
  AJAXPollingResponseForPreparingDesign,
  ShareToAppCheckoutResponse,
} from '@Panels/poster-publishing-options-panel/components/publishing-share-to-app-panel/publishing-share-to-app-panel.types';
import {asyncnoop} from '@Utils/general.util';

export const SHARE_TO_APP_UNDEFINED = 'undefined';
export const ORDER_REGEN_RESPONSE = 1;
const WEBSHARE_POLLING_REQUEST_TIMEOUT_MS = 180000;

export const onWebShareError = (): void => {
  showErrorOnPanel(window.i18next.t('pmwjs_error_message'));
};

export const isNewPremiumDownload = (response: ShareToAppCheckoutResponse): boolean => {
  return (
    typeof response.status !== 'undefined' &&
    (response.status === Status.NEW_ORDER || response.status === Status.CREDIT_USABLE) &&
    typeof response.checkout !== 'undefined' &&
    response.checkout['raw-price'] === 0
  );
};
const fetchFileFromURL = (url: string, productUsed: PosterProductDataForProcessDesign, index: number): Promise<File> => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('GET', url, true);
    xhr.responseType = 'blob';

    xhr.onload = (): void => {
      if (xhr.status >= 400) {
        reject(new Error('Network error'));
        return;
      }
      const blob: Blob = xhr.response as Blob;

      let ext: string;
      let mime: string;

      if (url.endsWith('.mp4')) {
        ext = 'mp4';
        mime = 'video/mp4';
      } else if (url.endsWith('.png')) {
        ext = 'png';
        mime = 'image/png';
      } else {
        ext = 'jpg';
        mime = 'image/jpeg';
      }

      const fileName = `${productUsed?.posterName ?? 'design'}_${index + 1}.${ext}`;
      const file = new File([blob], fileName, {type: mime});
      resolve(file);
    };

    xhr.onerror = (): void => {
      reject(new Error('Network error'));
    };
    xhr.timeout = 30000;
    xhr.ontimeout = (): void => {
      reject(new Error('Request timed out'));
    };
    xhr.onabort = (): void => {
      reject(new Error('Request aborted'));
    };

    window.PMW.addAjaxParamsForSubdomainToSubfolderRequests(xhr, true);
    xhr.send();
  });
};

export const sendXMLRequestForShareToApp = (
  urls: string[],
  productUsed: PosterProductDataForProcessDesign,
  setFilesToShare: (files: File[]) => void,
  setIsLoading: (isLoading: boolean) => void
): void => {
  Promise.all(urls.map((url, index) => fetchFileFromURL(url, productUsed, index)))
    .then((files) => {
      setFilesToShare(files);
      setIsLoading(false);
    })
    .catch(() => {
      onWebShareError();
    });
};

export const onShareToApp = (productUsed: PosterProductDataForProcessDesign, filesToShare: File[], setIsLoading: (isLoading: boolean) => void): void => {
  let title = `PosterMyWall | ${productUsed?.posterName}`;
  let text = window.i18next.t('pmwjs_made_with_postermywall');

  /**
   * @see https://stackoverflow.com/questions/60302475/can-i-share-a-file-with-web-share-api-on-safari/66126240#66126240
   * Apparently, text/title needs to be empty for sharing assets through iOS
   */
  if (window.PMW.isIOS()) {
    title = '';
    text = '';
  }

  void navigator
    .share({
      files: filesToShare,
      title,
      text,
    })
    .catch(() => {
      setIsLoading(false);
      // Not showing error here since simply closing the share dialog will also cause this
    });
};

export const onGenerationComplete = (
  data: AJAXPollingResponseForPreparingDesign,
  productUsed: PosterProductDataForProcessDesign,
  setFilesToShare: (files: File[]) => void,
  setIsLoading: (isLoading: boolean) => void
): void => {
  if (data.urls.length > 0) {
    sendXMLRequestForShareToApp(data.urls, productUsed, setFilesToShare, setIsLoading);
  } else {
    onWebShareError();
  }
};

export const doWebShare = (
  productUsed: PosterProductDataForProcessDesign,
  posterHashedId: string,
  isHighRes: boolean,
  setCanShare: (canShare: boolean) => void,
  setIsLoading: (isLoading: boolean) => void,
  setFilesToShare: (files: File[]) => void,
  pageHashedIds: string[] | null
): void => {
  setIsLoading(true);
  prepareDesignForWebShare(posterHashedId, isHighRes, pageHashedIds)
    .then(() => {
      return window.PMW.pollingAjaxCallAsync(asyncnoop, webShareDownloadReady.bind(null, !isHighRes, posterHashedId, pageHashedIds), WEBSHARE_POLLING_REQUEST_TIMEOUT_MS);
    })
    .then((response: AJAXPollingResponseForPreparingDesign) => {
      setCanShare(true);
      onGenerationComplete(response, productUsed, setFilesToShare, setIsLoading);
    })
    .catch(() => {
      onWebShareError();
    });
};

export const prepareDesignForWebShare = (posterHashedId: string, isHighRes: boolean, pageHashedIds: string[] | null): Promise<void> => {
  return window.PMW.writeLocal('socialmedia/prepareDesignForWebShare', {
    hashedId: posterHashedId,
    isHighres: isHighRes,
    phids: pageHashedIds,
  });
};

export const webShareDownloadReady = (isFreebie: boolean, posterHashedId: string, pageHashedIds: string[] | null): Promise<void> => {
  return window.PMW.readLocal('socialmedia/isWebShareDownloadReady', {
    hashedId: posterHashedId,
    isFreebie,
    phids: pageHashedIds,
  });
};
